import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { XIcon, GlobeIcon, DollarSignIcon, ChevronRightIcon, HomeIcon, LayoutGridIcon, TagIcon, TrendingUpIcon, StarIcon, PackageIcon, ShieldIcon, HeartIcon, UserIcon, LogOutIcon, ShoppingBagIcon, MapPinIcon, HeartPulseIcon, ActivityIcon, BrainIcon, ThermometerIcon, PillIcon, StethoscopeIcon, CreditCardIcon, BabyIcon, SparklesIcon, DropletsIcon } from 'lucide-react';
import { LoyaltyOnboardingBottomSheet } from './modals/LoyaltyOnboardingBottomSheet';
import { AuthBottomSheet } from './modals/AuthBottomSheet';
import { LanguageBottomSheet } from './modals/LanguageBottomSheet';
import { LoyaltyCard } from './account/LoyaltyCard';
import { useStore } from '../lib/store';
interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}
export function MobileMenu({
  isOpen,
  onClose
}: MobileMenuProps) {
  const navigate = useNavigate();
  const {
    language,
    setLanguage
  } = useStore();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [languageBottomSheetOpen, setLanguageBottomSheetOpen] = useState(false);
  const [authBottomSheetOpen, setAuthBottomSheetOpen] = useState(false);
  const [loyaltyBottomSheetOpen, setLoyaltyBottomSheetOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loyaltyProgress, setLoyaltyProgress] = useState(0);
  const [loyaltyData, setLoyaltyData] = useState<{
    memberId?: string;
    pointsBalance?: number;
    status?: 'active' | 'inactive';
    tier?: 'silver' | 'gold' | 'platinum';
    isEnrolled?: boolean;
  }>({});
  // Calculate loyalty progress from localStorage
  useEffect(() => {
    const savedData = localStorage.getItem('loyaltyOnboarding');
    if (savedData) {
      const data = JSON.parse(savedData);
      const requiredFields = [data.gender, data.maritalStatus, data.nationality, data.dateOfBirth, data.workingSector, data.city, data.district, data.preferredLanguage];
      const completedRequired = requiredFields.filter(field => field !== '').length;
      const progress = Math.round(completedRequired / requiredFields.length * 100);
      setLoyaltyProgress(progress);
      // If profile is complete, set loyalty member data
      if (progress === 100) {
        setLoyaltyData({
          memberId: '1234567890',
          pointsBalance: 3250,
          status: 'active',
          tier: 'gold',
          isEnrolled: true
        });
      } else {
        setLoyaltyData({
          isEnrolled: false
        });
      }
    }
  }, [loyaltyBottomSheetOpen]);
  const openAuthModal = () => {
    onClose();
    setAuthBottomSheetOpen(true);
  };
  const handleLanguageChange = (lang: string) => {
    setLanguage(lang as 'en' | 'ar');
  };
  const handleAuthSuccess = (isNewUser: boolean) => {
    setIsLoggedIn(true);
    setAuthBottomSheetOpen(false);
    if (isNewUser && loyaltyProgress < 100) {
      // Prompt to join loyalty
      setTimeout(() => setLoyaltyBottomSheetOpen(true), 500);
    }
  };
  const handleLoyaltyComplete = (data: any) => {
    localStorage.setItem('loyaltyOnboarding', JSON.stringify(data));
    setLoyaltyProgress(100);
    setLoyaltyBottomSheetOpen(false);
    // Update loyalty data to enrolled state
    setLoyaltyData({
      memberId: '1234567890',
      pointsBalance: 0,
      status: 'active',
      tier: 'silver',
      isEnrolled: true
    });
  };
  const conditions = [{
    icon: <HeartPulseIcon className="w-4 h-4 text-red-600" data-id="element-3390" data-mp="3u7s4z74evi15" />,
    name: 'Heart Health',
    slug: 'heart-health'
  }, {
    icon: <ActivityIcon className="w-4 h-4 text-blue-600" data-id="element-3391" data-mp="707wr5wu3cr4" />,
    name: 'Diabetes',
    slug: 'diabetes'
  }, {
    icon: <BrainIcon className="w-4 h-4 text-purple-600" data-id="element-3392" data-mp="2drz0cmeiji8j" />,
    name: 'Mental Health',
    slug: 'mental-health'
  }, {
    icon: <ThermometerIcon className="w-4 h-4 text-orange-600" data-id="element-3393" data-mp="28u7cq4kwiia5" />,
    name: 'Pain Relief',
    slug: 'pain-relief'
  }, {
    icon: <PillIcon className="w-4 h-4 text-green-600" data-id="element-3394" data-mp="2f7tm4vig7pec" />,
    name: 'Digestive Health',
    slug: 'digestive-health'
  }, {
    icon: <StethoscopeIcon className="w-4 h-4 text-cyan-600" data-id="element-3395" data-mp="2k7z7jty4kfnz" />,
    name: 'Respiratory',
    slug: 'respiratory'
  }];
  const handleConditionClick = (slug: string) => {
    onClose();
    navigate(`/category/${slug}`);
  };
  const handleCategoryClick = (slug: string) => {
    onClose();
    navigate(`/category/${slug}`);
  };
  return <>
      {/* Full Page Menu */}
      <div className={`fixed inset-0 bg-white z-[100] transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : '-translate-x-full'} flex flex-col h-full w-full overflow-hidden`} data-id="element-3396" data-mp="21golpr79at2y">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-gray-100 bg-white sticky top-0 z-10" data-id="element-3397" data-mp="18q6hgmusqlny">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/8onJCNEDDvmSU1TZkPymCw/English.png" alt="DrNutrition" className="h-10" data-id="element-3398" data-mp="3r1dhe3bj5qnw" />
          <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 active:bg-gray-200 transition-colors" aria-label="Close menu" data-id="element-3399" data-mp="3my7b4vof3zhf">
            <XIcon className="w-6 h-6 text-gray-900" data-id="element-3400" data-mp="383azs9lbaw04" />
          </button>
        </div>
        {/* Menu Content - Scrollable */}
        <div className="flex-1 overflow-y-auto bg-white" data-id="element-3401" data-mp="33b9fq1hwk0zj">
          {/* Welcome Section */}
          <div className="px-4 py-8 bg-gradient-to-b from-gray-50 to-white border-b border-gray-100" data-id="element-3402" data-mp="2zbipbywafmvj">
            <div className="flex items-center gap-5" data-id="element-3403" data-mp="2ucnyqas2wu0v">
              <div className="w-16 h-16 rounded-2xl bg-[#1E2B55] flex items-center justify-center text-white shadow-lg shadow-blue-900/10" data-id="element-3404" data-mp="2pc9yvg0msqdx">
                <UserIcon className="w-8 h-8" data-id="element-3405" data-mp="2kehyrypu1xyn" />
              </div>
              <div data-id="element-3406" data-mp="2apummu01i69j">
                <p className="text-xl font-black text-gray-900 leading-tight" data-id="element-3407" data-mp="2aojg4mf7mkeq">
                  Welcome to <br data-id="element-3408" data-mp="2foxpc7sjs0ec" />
                  <span className="text-[#1E2B55]" data-id="element-3409" data-mp="2fpgwctgnt4dl">Dr. Nutrition</span>
                </p>
                <div className="flex items-center gap-4 mt-2" data-id="element-3410" data-mp="21pxycthkb9f0">
                  <button onClick={openAuthModal} className="text-sm font-extrabold text-[#1E2B55] bg-blue-50 px-4 py-1.5 rounded-full active:scale-95 transition-transform" data-id="element-3411" data-mp="25q85wal067vz">
                    Sign In
                  </button>
                  <button onClick={openAuthModal} className="text-sm font-bold text-gray-500 hover:text-gray-700" data-id="element-3412" data-mp="2knqth9ly2f1u">
                    Register
                  </button>
                </div>
              </div>
            </div>
          </div>
          {/* Language, Country, Currency Section */}
          <div className="p-4 border-b border-gray-200" data-id="element-3413" data-mp="3h6ga0gg31r9r">
            <div className="space-y-3" data-id="element-3414" data-mp="15qed4j80mbqe">
              {/* Language Selector */}
              <button onClick={() => setLanguageBottomSheetOpen(true)} className="flex items-center justify-between w-full" data-id="element-3415" data-mp="10rl16v8dmssc">
                <div className="flex items-center space-x-2" data-id="element-3416" data-mp="1ffpko1f978c6">
                  <GlobeIcon className="w-4 h-4 text-gray-500" data-id="element-3417" data-mp="1ah4nybz7n6sh" />
                  <span className="text-sm" data-id="element-3418" data-mp="mrtttdcd8anb">Language</span>
                </div>
                <div className="flex items-center text-sm text-gray-700 bg-gray-100 px-2 py-1 rounded" data-id="element-3419" data-mp="mtwh3pm4vpoc">
                  {language === 'en' ? 'English' : 'Arabic'}
                  <ChevronRightIcon className="w-4 h-4 ml-1" data-id="element-3420" data-mp="wqre242rdi0i" />
                </div>
              </button>
            </div>
          </div>
          {/* Menu Categories */}
          <div className="p-4 border-b border-gray-100" data-id="element-3421" data-mp="284n2k3fjz34e">
            <div className="grid grid-cols-3 gap-2 mb-4" data-id="element-3422" data-mp="28799q1yj7gm3">
              <button onClick={() => handleCategoryClick('best-sellers')} className="flex flex-col items-center p-3 rounded-xl bg-amber-50 border border-amber-100 active:scale-95 transition-transform" data-id="element-3423" data-mp="2350gd4hqycjg">
                <TrendingUpIcon className="w-5 h-5 text-amber-600 mb-1" data-id="element-3424" data-mp="5g4zcdx9n795" />
                <span className="text-[10px] font-bold text-amber-900" data-id="element-3425" data-mp="ag9iycth0i3l">
                  Best Sellers
                </span>
              </button>
              <button onClick={() => handleCategoryClick('new-arrivals')} className="flex flex-col items-center p-3 rounded-xl bg-blue-50 border border-blue-100 active:scale-95 transition-transform" data-id="element-3426" data-mp="k7nlktgpxy0y">
                <ShoppingBagIcon className="w-5 h-5 text-blue-600 mb-1" data-id="element-3427" data-mp="t8lm31ry7nes" />
                <span className="text-[10px] font-bold text-blue-900" data-id="element-3428" data-mp="y63tgnqbhuko">
                  New Arrivals
                </span>
              </button>
              <button onClick={() => handleCategoryClick('deals-offers')} className="flex flex-col items-center p-3 rounded-xl bg-red-50 border border-red-100 active:scale-95 transition-transform" data-id="element-3429" data-mp="2v1ruix98fzem">
                <TagIcon className="w-5 h-5 text-red-600 mb-1" data-id="element-3430" data-mp="2no0tczqunizx" />
                <span className="text-[10px] font-bold text-red-900" data-id="element-3431" data-mp="2ip7cqyg9nbvj">
                  Hot Deals
                </span>
              </button>
            </div>
            <button onClick={() => {
            onClose();
            navigate('/categories');
          }} className="w-full bg-[#1E2B55] text-white text-sm font-bold py-3.5 rounded-xl shadow-lg shadow-blue-900/20 active:scale-[0.98] transition-all flex items-center justify-center gap-2" data-id="element-3432" data-mp="2dro022z7v75p">
              <LayoutGridIcon className="w-4 h-4" data-id="element-3433" data-mp="2wgukbufns6tm" />
              View All Categories
            </button>
          </div>
          {/* Shop By Categories */}
          <div className="p-4 border-b border-gray-200" data-id="element-3434" data-mp="1q1tmfj72peke">
            <h3 className="text-xs font-bold text-gray-500 mb-4 tracking-wider uppercase" data-id="element-3435" data-mp="1l0xgz172pkcv">
              Shop by Category
            </h3>
            <div className="grid grid-cols-1 gap-1" data-id="element-3436" data-mp="6vz2r7y8ful6">
              {[{
              name: 'Medicines (OTC)',
              slug: 'medicines',
              icon: <PillIcon className="w-5 h-5 text-blue-600" data-id="element-3437" data-mp="gmacxc8if00i" />
            }, {
              name: 'Vitamins & Supplements',
              slug: 'vitamins-supplements',
              icon: <ActivityIcon className="w-5 h-5 text-green-600" data-id="element-3438" data-mp="pkm177lwllps" />
            }, {
              name: 'Personal Care',
              slug: 'personal-care',
              icon: <UserIcon className="w-5 h-5 text-purple-600" data-id="element-3439" data-mp="zis51ur9t1xa" />
            }, {
              name: 'Mother & Baby',
              slug: 'mother-baby',
              icon: <BabyIcon className="w-5 h-5 text-pink-600" data-id="element-3440" data-mp="31cj14hkss1vm" />
            }, {
              name: 'Skin Care',
              slug: 'skin-care',
              icon: <SparklesIcon className="w-5 h-5 text-rose-500" data-id="element-3441" data-mp="1btswtb8s401e" />
            }, {
              name: 'Hair Care',
              slug: 'hair-care',
              icon: <DropletsIcon className="w-5 h-5 text-cyan-500" data-id="element-3442" data-mp="124ssrdff6egc" />
            }, {
              name: 'Medical Devices',
              slug: 'medical-devices',
              icon: <StethoscopeIcon className="w-5 h-5 text-slate-600" data-id="element-3443" data-mp="t44fwm1rik51" />
            }].map(category => <button key={category.slug} onClick={() => handleCategoryClick(category.slug)} className="flex items-center py-3.5 px-3 rounded-xl hover:bg-gray-50 active:bg-gray-100 transition-colors w-full text-left group" data-id="element-3444" data-mp="y37j00y6mgad">
                  <div className="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center mr-4 group-active:scale-95 transition-transform" data-id="element-3445" data-mp="2djg2bto6qew9">
                    {category.icon}
                  </div>
                  <span className="text-sm font-bold text-gray-800 flex-1" data-id="element-3446" data-mp="2h2nepr1wi8cd">
                    {category.name}
                  </span>
                  <ChevronRightIcon className="w-5 h-5 text-gray-300 group-hover:text-gray-500 transition-colors" data-id="element-3447" data-mp="2c4lu31otu0ys" />
                </button>)}
            </div>
          </div>
          {/* Browse by Condition Section */}
          <div className="p-4 border-b border-gray-100" data-id="element-3448" data-mp="2zsmvzrri2j58">
            <h3 className="text-xs font-bold text-gray-500 mb-4 tracking-wider uppercase" data-id="element-3449" data-mp="2zu7rbjln2645">
              Browse by Condition
            </h3>
            <div className="grid grid-cols-2 gap-3" data-id="element-3450" data-mp="2q1s86iylvbho">
              {conditions.map((condition, index) => <button key={index} onClick={() => handleConditionClick(condition.slug)} className="flex flex-col items-center justify-center p-4 rounded-2xl bg-gray-50 border border-gray-100 active:bg-blue-50 active:border-blue-100 transition-all group" data-id="element-3451" data-mp="2m1i2z8in0p3v" data-mpi={index}>
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center mb-2 shadow-sm group-active:scale-90 transition-transform" data-id="element-3452" data-mp="10at8p0am2xw3">
                    {condition.icon}
                  </div>
                  <span className="text-[11px] font-bold text-gray-700 text-center leading-tight" data-id="element-3453" data-mp="19c18jmss06sa">
                    {condition.name}
                  </span>
                </button>)}
            </div>
          </div>
          {/* Help & Support */}
          <div className="p-4 border-b border-gray-200" data-id="element-3454" data-mp="1o5wb6b2c8zv1">
            <h3 className="text-xs font-bold text-gray-500 mb-3" data-id="element-3455" data-mp="1sy5wfdsidbqz">
              HELP & SUPPORT
            </h3>
            <ul className="space-y-1" data-id="element-3456" data-mp="1x00wkeypdj36">
              <li data-id="element-3457" data-mp="1wyzlwbllmylz">
                <button onClick={() => {
                onClose();
                navigate('/contact-us');
              }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3458" data-mp="1x1lp5z1girye">
                  <span className="text-sm" data-id="element-3459" data-mp="1t958t3jc4j7l">Contact Us</span>
                </button>
              </li>
              <li data-id="element-3460" data-mp="1oblk19p0go1e">
                <button onClick={() => {
                onClose();
                navigate('/faq');
              }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3461" data-mp="1oa0wi41465d1">
                  <span className="text-sm" data-id="element-3462" data-mp="1accbmw71qy7z">FAQs</span>
                </button>
              </li>
              <li data-id="element-3463" data-mp="15eu1j2hamcye">
                <button onClick={() => {
                onClose();
                navigate('/shipping-delivery');
              }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3464" data-mp="15fdiogz4cxbd">
                  <span className="text-sm" data-id="element-3465" data-mp="2vr9rse7cd7u3">Shipping & Delivery</span>
                </button>
              </li>
              <li data-id="element-3466" data-mp="2y9v16w9n5fcs">
                <button onClick={() => {
                onClose();
                navigate('/return-policy');
              }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3467" data-mp="2y9bte8dpg7ch">
                  <span className="text-sm" data-id="element-3468" data-mp="2taihulhzaqey">Return Policy</span>
                </button>
              </li>
            </ul>
          </div>
          {/* Services & Tools */}
          <div className="p-4 border-b border-gray-200" data-id="element-3469" data-mp="3h2p58v5q7n28">
            <h3 className="text-xs font-bold text-gray-500 mb-3" data-id="element-3470" data-mp="3ly4h5hk6b8w0">
              SERVICES & TOOLS
            </h3>
            <ul className="space-y-1" data-id="element-3471" data-mp="387j78icp69a9">
              <li data-id="element-3472" data-mp="3c9ml1dmu9pyp">
                <button onClick={() => {
                onClose();
                navigate('/prescription');
              }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3473" data-mp="3c94dvlyn1rc0">
                  <span className="text-sm" data-id="element-3474" data-mp="ig9of0z95895">Upload Prescription</span>
                </button>
              </li>
              <li data-id="element-3475" data-mp="eeoglu1krk5t">
                <button onClick={() => {
                onClose();
                navigate('/prescription-history');
              }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3476" data-mp="eg9b5jnw333o">
                  <span className="text-sm" data-id="element-3477" data-mp="3vo38rjcrxws2">Prescription History</span>
                </button>
              </li>
              <li data-id="element-3478" data-mp="3qus3hmnm1o29">
                <button onClick={() => {
                onClose();
                navigate('/branches');
              }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3479" data-mp="3qu8ks3q4yvn6">
                  <span className="text-sm" data-id="element-3480" data-mp="3cvsdobod334i">Our Branches</span>
                </button>
              </li>
              <li data-id="element-3481" data-mp="37y8y98fxgkaz">
                <button onClick={() => {
                onClose();
                navigate('/doctor-consultation');
              }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3482" data-mp="37ztl0bw02yya">
                  <span className="text-sm" data-id="element-3483" data-mp="37pvo1uwidjyv">Doctor Consultation</span>
                </button>
              </li>
              <li data-id="element-3484" data-mp="2f32kz5vnc8lp">
                <button onClick={() => {
                onClose();
                navigate('/medication-finder');
              }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3485" data-mp="2f3kkcbdlv2xu">
                  <span className="text-sm" data-id="element-3486" data-mp="2tsgt6anhkjzi">Medication Finder</span>
                </button>
              </li>
            </ul>
          </div>
          {/* Legal & Policies */}
          <div className="p-4 border-b border-gray-200" data-id="element-3487" data-mp="21bnyuq6fzraf">
            <h3 className="text-xs font-bold text-gray-500 mb-3" data-id="element-3488" data-mp="21acsqjpiy7g5">
              LEGAL & POLICIES
            </h3>
            <ul className="space-y-1" data-id="element-3489" data-mp="3fie3izo3pkko">
              <li data-id="element-3490" data-mp="3bgslkzwpmg3m">
                <button onClick={() => {
                onClose();
                navigate('/privacy-policy');
              }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3491" data-mp="3bharypd36ap9">
                  <span className="text-sm" data-id="element-3492" data-mp="2b2py7uvasnx">Privacy Policy</span>
                </button>
              </li>
              <li data-id="element-3493" data-mp="3ug6i9pltq4xt">
                <button onClick={() => {
                onClose();
                navigate('/terms-conditions');
              }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3494" data-mp="3ueloi27c2q10">
                  <span className="text-sm" data-id="element-3495" data-mp="m0d2jczbgxht">Terms & Conditions</span>
                </button>
              </li>
              <li data-id="element-3496" data-mp="h21rx0176iyj">
                <button onClick={() => {
                onClose();
                navigate('/refund-policy');
              }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3497" data-mp="gydys1znzykc">
                  <span className="text-sm" data-id="element-3498" data-mp="qnuoaqix9e0k">Refund Policy</span>
                </button>
              </li>
            </ul>
          </div>
        </div>
        {/* Bottom Section */}
        <div className="p-4 border-t border-gray-200" data-id="element-3499" data-mp="gxhus8t4qaia">
          <a href="#" className="flex items-center text-red-500 font-medium hover:text-red-600" data-id="element-3500" data-mp="lori32r7h1r0">
            <LogOutIcon className="w-4 h-4 mr-2" data-id="element-3501" data-mp="7yo3mxykicc4" />
            <span className="text-sm" data-id="element-3502" data-mp="cwpit7svhrib">Sign Out</span>
          </a>
        </div>
      </div>
      {/* Language Bottom Sheet */}
      <LanguageBottomSheet isOpen={languageBottomSheetOpen} onClose={() => setLanguageBottomSheetOpen(false)} currentLanguage={language} onLanguageChange={handleLanguageChange} data-id="element-3503" data-mp="3u4b8lu0hs62d" />
      {/* Auth Bottom Sheet */}
      <AuthBottomSheet isOpen={authBottomSheetOpen} onClose={() => setAuthBottomSheetOpen(false)} onAuthSuccess={handleAuthSuccess} data-id="element-3504" data-mp="21tfng26kgfg" />
      {/* Loyalty Onboarding Bottom Sheet */}
      <LoyaltyOnboardingBottomSheet isOpen={loyaltyBottomSheetOpen} onClose={() => setLoyaltyBottomSheetOpen(false)} onComplete={handleLoyaltyComplete} data-id="element-3505" data-mp="3ocffz5jfpu8w" />
    </>;
}